import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAgencyAdmin } from "@/features/agency/hooks";

/**
 * Gates a route on (a) an authenticated session and (b) the current org
 * having `org_type === 'agency'` AND the user holding `owner` or `admin` role.
 *
 * Mirrors PlatformAdminRoute:
 *   - Loading shell while session/org data is in flight.
 *   - Redirect to `/login` if unauthenticated.
 *   - Redirect to `/dashboard` if authenticated but not an agency admin.
 *
 * RLS enforces the real boundary server-side; this is the UX gate.
 */
export function AgencyRoute({ children }: { children: ReactNode }) {
  const { session, isLoading: authLoading } = useAuth();
  const { isAgencyAdmin, isLoading: agencyLoading } = useIsAgencyAdmin();
  const location = useLocation();

  if (authLoading || agencyLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAgencyAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
